import { useSyncExternalStore } from "react";

export interface CapturedError {
  at: number;
  kind: "render" | "error" | "unhandledrejection";
  name: string;
  message: string;
  where?: string | undefined;
}

const MAX = 20;
const KEY = "tank.errors";
let entries: CapturedError[] = load();
const subscribers = new Set<() => void>();

function load(): CapturedError[] {
  try {
    const raw = sessionStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as CapturedError[]) : [];
  } catch {
    return []; // private mode, blocked storage, or something else wrote over the key
  }
}

function save(): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(entries));
  } catch {
    // Not being able to remember an error is not a reason to throw another one.
  }
}

function emit(): void {
  for (const s of subscribers) s();
}

function subscribe(fn: () => void): () => void {
  subscribers.add(fn);
  return () => {
    subscribers.delete(fn);
  };
}

function snapshot(): CapturedError[] {
  return entries;
}

/** Errors this tab has survived, newest first. */
export function useCapturedErrors(): CapturedError[] {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}

export function clearCapturedErrors(): void {
  entries = [];
  save();
  emit();
}

/** Records an error for the diagnostics page. Never records anything but the class, message and origin. */
export function captureError(kind: CapturedError["kind"], err: unknown, where?: string): void {
  const e =
    err instanceof Error ? { name: err.name, message: err.message } : { name: "Error", message: String(err) };
  entries = [{ at: Date.now(), kind, ...e, where }, ...entries].slice(0, MAX);
  save();
  emit();
}

let installed = false;

/**
 * Catches what a React error boundary cannot: a throw from an event handler, a timer, or a promise nobody
 * awaited. Without this the only trace is a line in a console the person will never open, and a support
 * conversation becomes guesswork.
 */
export function installErrorCapture(): void {
  if (installed) return;
  installed = true;
  window.addEventListener("error", (e) => {
    captureError("error", e.error ?? e.message, e.filename ? `${e.filename}:${e.lineno}` : undefined);
  });
  window.addEventListener("unhandledrejection", (e) => {
    captureError("unhandledrejection", e.reason);
  });
}
